import { isDanhyMobileDevice } from "./utils";
import { getDanhyStoreUrl, openDanhyAppDeeplink } from "./_DanhyDeeplink";

const STORAGE_KEY = "hm_danhy_app_ad_dismissed_until";
const DISMISS_DAYS = 30;
const DISMISS_MS = DISMISS_DAYS * 24 * 60 * 60 * 1000;
const SHOW_DELAY_MS = 500;
const CLOSE_MS = 420;

export default class DanhyAppAd {
  constructor() {
    this.$ad = $("#booking-danhy-app-ad");
    this.$cta = this.$ad.find(".booking-danhy-app-ad__cta");
    this.$strip = $(".booking-form-crm-danhy").first();
    this._shown = false;
    this._observer = null;
    this._showTimer = null;
    this._closeTimer = null;

    this.hide(true);

    if (!this.$ad.length) {
      return;
    }
    if (!isDanhyMobileDevice()) {
      return;
    }
    if (!this.$strip.length || this.$strip.prop("hidden")) {
      return;
    }
    if (this.isDismissed()) {
      return;
    }

    this.bindStoreUrl();
    this.bindEvents();
    this.observeStrip();
  }

  hide(immediate = false) {
    if (!this.$ad.length) {
      return;
    }

    this.clearCloseTimer();
    this.$ad.removeClass("is-open");
    $("body").removeClass("danhy-app-ad-open");

    const conceal = () => {
      this.$ad.attr("hidden", true).attr("aria-hidden", "true");
    };

    if (immediate || this.prefersReducedMotion()) {
      conceal();
      return;
    }

    this._closeTimer = window.setTimeout(conceal, CLOSE_MS);
  }

  show() {
    if (this._shown || this.isDismissed() || !this.$ad.length) {
      return;
    }
    if (!this.$strip.length || this.$strip.prop("hidden")) {
      return;
    }
    this._shown = true;
    this.clearShowTimer();
    this.disconnectObserver();
    this.$ad.removeAttr("hidden").attr("aria-hidden", "false");
    $("body").addClass("danhy-app-ad-open");

    const open = () => {
      this.$ad.addClass("is-open");
    };

    if (this.prefersReducedMotion()) {
      open();
      return;
    }

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(open);
    });
  }

  close() {
    this.hide();
    this.persistDismiss();
    this.disconnectObserver();
  }

  bindEvents() {
    this.$ad
      .off("click.danhyAppAdClose")
      .on("click.danhyAppAdClose", ".booking-danhy-app-ad-close", (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.close();
      });

    this.$cta.off("click.danhyAppAdCta").on("click.danhyAppAdCta", (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.close();
      openDanhyAppDeeplink();
    });

    $(document)
      .off("keydown.danhyAppAd")
      .on("keydown.danhyAppAd", (event) => {
        if (event.key === "Escape" && !this.$ad.prop("hidden")) {
          this.close();
        }
      });
  }

  bindStoreUrl() {
    const url = getDanhyStoreUrl();
    if (!url) {
      this.$cta.attr("href", "#");
      return;
    }
    this.$cta.attr("href", url);
  }

  observeStrip() {
    const el = this.$strip.get(0);
    if (!el || typeof IntersectionObserver === "undefined") {
      this.scheduleShow();
      return;
    }

    this._observer = new IntersectionObserver(
      (entries) => {
        if (this._shown) {
          return;
        }
        const ready = entries.some(
          (entry) => entry.isIntersecting && entry.intersectionRatio >= 0.6,
        );
        if (ready) {
          this.scheduleShow();
          return;
        }
        this.clearShowTimer();
      },
      {
        threshold: [0.6, 0.75, 1],
        rootMargin: "0px 0px -18% 0px",
      },
    );
    this._observer.observe(el);
  }

  scheduleShow() {
    if (this._shown || this._showTimer) {
      return;
    }
    this._showTimer = window.setTimeout(() => {
      this._showTimer = null;
      this.show();
    }, SHOW_DELAY_MS);
  }

  clearShowTimer() {
    if (this._showTimer) {
      window.clearTimeout(this._showTimer);
      this._showTimer = null;
    }
  }

  clearCloseTimer() {
    if (this._closeTimer) {
      window.clearTimeout(this._closeTimer);
      this._closeTimer = null;
    }
  }

  prefersReducedMotion() {
    return (
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  }

  disconnectObserver() {
    this.clearShowTimer();
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
  }

  isDismissed() {
    const until = this.readDismissedUntil();
    return Number.isFinite(until) && Date.now() < until;
  }

  persistDismiss() {
    const until = Date.now() + DISMISS_MS;
    try {
      localStorage.setItem(STORAGE_KEY, String(until));
    } catch (e) {
      // Ignore quota / private-mode failures; cookie still covers path=/.
    }
    this.setCookie(STORAGE_KEY, String(until), DISMISS_DAYS);
  }

  readDismissedUntil() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = Number(stored);
        if (Number.isFinite(parsed)) {
          return parsed;
        }
      }
    } catch (e) {
      // Fall through to cookie.
    }

    const cookieValue = this.getCookie(STORAGE_KEY);
    const parsed = Number(cookieValue);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  setCookie(name, value, days) {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires.toUTCString()};path=/`;
  }

  getCookie(name) {
    const nameEQ = `${name}=`;
    const parts = document.cookie.split(";");
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i].trim();
      if (part.indexOf(nameEQ) === 0) {
        return decodeURIComponent(part.substring(nameEQ.length));
      }
    }
    return "";
  }
}
